import Dropzone from 'dropzone';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { API_URL_PREFIX, BasicService } from '../../services/basic.service';
import { UploadService } from '../../services/upload.service';

export class UploadByChunkHelper {
  constructor(
    private isUploading$: BehaviorSubject<boolean>,
    private uploadProgress$: BehaviorSubject<number>,
    private uploadService: UploadService,
    private alcmeonContext: AlcmeonContextService,
    private basicService: BasicService
  ) {
    this.divRandomIdentifier = btoa(Math.random().toString()).slice(0, 5);
  }

  divRandomIdentifier: string;
  videoId: number;
  maxFileSizeMb: number;
  acceptedFiles: string;
  dropzone: Dropzone;
  baseUrl = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/upload-by-chunk`;
  videoId$: Subject<number>;

  init(maxFileSizeMb, acceptedFiles) {
    this.maxFileSizeMb = maxFileSizeMb;
    this.acceptedFiles = acceptedFiles;
    if (!this.wasDropzoneCreated()) {
      this.createDropzone();
    }
  }

  createDropzone() {
    this.dropzone = new Dropzone(`#${this.divRandomIdentifier}`, {
      url: this.baseUrl,
      chunking: true,
      forceChunking: true,
      retryChunks: false,
      createImageThumbnails: false,
      disablePreviews: true,
      maxfilesize_format: this.maxFileSizeMb,
      autoProcessQueue: false, // to have the time to create the video in db and add its id to the url
      acceptedFiles: this.acceptedFiles,
      maxFiles: 1,
      headers: {
        Authorization: `Bearer ${this.alcmeonContext.jwt}`,
      },
    });

    this.dropzone.on('addedfile', file => this.handleAddedFile(file));

    this.dropzone.on('uploadprogress', file => this.handleProgress(file.upload.progress));

    this.dropzone.on('success', file => this.handleSuccess(file));

    this.dropzone.on('error', (file, message) => this.handleError(file, message));
  }

  getDivRandomIdentifier() {
    // Called in single-message-editor/single-message-editor.component.html
    return this.divRandomIdentifier;
  }

  uploadVideo() {
    // To prevent starting another upload when another one is ongoing
    if (!this.videoId) {
      this.openFileDialog();
    }
    this.videoId$ = new Subject<number>();
    return this.videoId$;
  }

  openFileDialog() {
    this.dropzone.hiddenFileInput.click();
  }

  clean(file, doDelete = true) {
    // When deleteVideoChunks fails, we should not recall it.
    if (doDelete) {
      this.deleteVideoAndChunks(file);
    }
    this.resetProgressBar();
    this.removeFile(file);
  }

  fetchCorrespondingFileFromDropzone(videoId) {
    return this.dropzone?.files?.find(file => file.videoId === videoId);
  }

  removeFileFromVideoId(videoId) {
    const file = this.fetchCorrespondingFileFromDropzone(videoId);

    // no dropzone and no file when editing publication
    if (file) {
      this.removeFile(file);
    }
  }

  private removeFile(file) {
    this.dropzone.removeFile(file);
    this.videoId = null;
  }

  private wasDropzoneCreated() {
    return !!this.dropzone;
  }

  private resetProgressBar() {
    this.isUploading$.next(false);
    this.uploadProgress$.next(0);
  }

  private deleteVideoAndChunks(file) {
    this.uploadService.deleteVideoAndChunks(file, this.videoId).subscribe(
      () => {
        /* */
      },
      () => this.clean(file, false)
    );
  }

  private handleSuccess(file) {
    this.uploadService.reconstructVideo(file, this.videoId).subscribe(
      () => {
        file.videoId = this.videoId;
        this.videoId$.next(this.videoId);
        this.resetProgressBar();
        this.videoId$.complete();
      },
      () => this.clean(file)
    );
  }

  private handleError(file, message) {
    // only do delete if some chunks were uploaded
    const doDelete = !!file.upload.chunks;
    this.clean(file, doDelete);
    if (message === 'media/upload/too-big/limitation') {
      this.basicService.presentAlert({
        subHeader: `${this.basicService.translate('media/upload/too-big/limitation', {
          max_size: `${this.maxFileSizeMb}`,
        })}`,
        buttons: [this.basicService.translate('m/ok')],
      });
    } else {
      this.basicService.presentAlert({
        subHeader: `${this.basicService.translate('media/upload/dropzone-error')}`,
        buttons: [this.basicService.translate('m/ok')],
      });
    }
  }

  private handleAddedFile(file) {
    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.handleError(file, 'media/upload/too-big/limitation');
    } else {
      this.isUploading$.next(true);
      this.uploadService.createVideoInDb(file).subscribe(
        videoId => {
          this.videoId = videoId;
          this.dropzone.options.url = `${this.baseUrl}/${videoId}`;
          // given that autoProcessQueue is set to false, we need to trigger processing
          this.dropzone.processQueue();
        },
        () => this.clean(file)
      );
    }
  }

  private handleProgress(fileProgress) {
    this.uploadProgress$.next(fileProgress / 100);
  }
}
